import React, { useState, useEffect, useCallback } from 'react';
import { marketsApi, productsApi, marketProductsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './MarketProducts.css';

const CATEGORIES = [
  { value: 'cc_bev', label: 'CC Beverages' },
  { value: 'cc_veg', label: 'CC Fermented Veg' },
  { value: 'cc_brine', label: 'CC Brines' },
  { value: 'cc_keg', label: 'CC Kegs' },
  { value: 'ar_frozen', label: 'AR Frozen & Prepared' },
  { value: 'ar_chicken', label: 'AR Chicken' },
  { value: 'ar_pork', label: 'AR Pork' },
  { value: 'ar_refrigerated', label: 'AR Refrigerated' },
];

export default function MarketProducts() {
  const [markets, setMarkets] = useState([]);
  const [products, setProducts] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [selectedMarket, setSelectedMarket] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mData, pData] = await Promise.all([
        marketsApi.getAll(),
        productsApi.getAll(),
      ]);
      const activeMarkets = (mData.markets || []).filter(m => m.is_active);
      const activeProducts = (pData.products || []).filter(p => p.is_active);
      setMarkets(activeMarkets);
      setProducts(activeProducts);
      if (activeMarkets.length > 0) {
        setSelectedMarket(activeMarkets[0].id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedMarket) return;
    loadAssignments(selectedMarket);
  }, [selectedMarket]);

  async function loadAssignments(marketId) {
    setError('');
    try {
      const data = await marketProductsApi.getForMarket(marketId);
      const map = {};
      (data.market_products || []).forEach(mp => {
        map[mp.product_id] = mp.is_active;
      });
      setAssignments(map);
    } catch (e) {
      setError(e.message);
    }
  }

  async function toggleProduct(productId, currentValue) {
    setSaving(productId);
    const newValue = !currentValue;
    setAssignments(a => ({ ...a, [productId]: newValue }));
    try {
      await marketProductsApi.toggle(selectedMarket, productId, newValue);
    } catch (e) {
      setAssignments(a => ({ ...a, [productId]: currentValue }));
      setError(e.message);
    } finally {
      setSaving('');
    }
  }

  async function selectAllInCategory(categoryProducts, currentlyAllSelected) {
    const newValue = !currentlyAllSelected;
    const updates = {};
    categoryProducts.forEach(p => { updates[p.id] = newValue; });
    setAssignments(a => ({ ...a, ...updates }));
    try {
      await Promise.all(
        categoryProducts.map(p => marketProductsApi.toggle(selectedMarket, p.id, newValue))
      );
    } catch (e) {
      setError(e.message);
      await loadAssignments(selectedMarket);
    }
  }

  const selectedMarketName = markets.find(m => m.id === selectedMarket)?.name || '';
  const activeCount = Object.values(assignments).filter(Boolean).length;

  const productsByCategory = CATEGORIES.map(cat => ({
    ...cat,
    products: products.filter(p => p.category === cat.value),
  })).filter(cat => cat.products.length > 0);

  return (
    <div className="page">
      <AdminPageHeader
        title="Market Products"
        subtitle={selectedMarketName ? `${activeCount} products assigned to ${selectedMarketName}` : 'Select a market'}
      />
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        <div className="market-selector">
          <label className="selector-label">Select Market</label>
          <select
            className="market-select"
            value={selectedMarket}
            onChange={(e) => setSelectedMarket(e.target.value)}
          >
            {markets.map(m => (
              <option key={m.id} value={m.id}>{m.name} — {m.day_of_week}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : (
          <div className="categories-grid">
            {productsByCategory.map(cat => {
              const allSelected = cat.products.every(p => !!assignments[p.id]);
              const someSelected = cat.products.some(p => !!assignments[p.id]);
              return (
                <div key={cat.value} className="category-card">
                  <div className="category-header">
                    <span>{cat.label}</span>
                    <button
                      className={`select-all-btn ${allSelected ? 'deselect' : ''}`}
                      onClick={() => selectAllInCategory(cat.products, allSelected)}
                    >
                      {allSelected ? 'Deselect All' : someSelected ? 'Select All' : 'Select All'}
                    </button>
                  </div>
                  <div className="product-list">
                    {cat.products.map(product => {
                      const isActive = !!assignments[product.id];
                      const isSaving = saving === product.id;
                      return (
                        <label key={product.id} className={`product-row ${isActive ? 'assigned' : ''} ${isSaving ? 'saving' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isActive}
                            onChange={() => toggleProduct(product.id, isActive)}
                            disabled={isSaving}
                          />
                          <span className="product-name">{product.name}</span>
                          {product.size && <span className="product-size">{product.size}</span>}
                          {isSaving && <span className="saving-indicator">...</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
